import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import type { Project } from 'api';
import {
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import type { DataTableColumn } from 'shared/components/DataTable';
import { DataTable, NonScrollingTableBox } from 'shared/components/DataTable';
import { HeroContainer } from 'shared/components/HeroContainer';
import {
  HeroActionButton,
  HeroContent,
  HeroDecorCircleBottom,
  HeroDecorCircleTop,
  HeroSubtitle,
} from 'shared/components/HeroContainer';
import { SearchableMultiSelect } from 'shared/components/SearchableMultiSelect';
import { ROUTE_PATHS } from 'shared/constants/routes';
import { usePermissions } from 'shared/hooks/usePermissions';

import { useProjectActions } from '../hooks/useProjectActions';
import {
  AccessChip,
  ActionCell,
  ApplyButton,
  BillingChip,
  ClearFiltersLink,
  ClearFiltersRow,
  ColorDot,
  DeleteMenuItem,
  FilterGroup,
  FilterWrapper,
  ColWidthBox,
  FILTER_FIELD_TEXT,
  NameCell,
  PageRoot,
  projectsTableContainerSx,
  ProjectNameText,
  RowMenuButton,
  RowMenuDivider,
  RowMenuItem,
  rowMenuSlotProps,
  SearchFieldBox,
  SearchIconWrap,
  SearchInput,
  StatusChip,
  TrackedText,
} from '../projects.styles';

import { ProjectFormModal } from './ProjectFormModal';

const STATUS_OPTIONS = [
  { id: 'Active', name: 'Active' },
  { id: 'Inactive', name: 'Inactive' },
  { id: 'OnHold', name: 'On Hold' },
];
const ACCESS_OPTIONS = [
  { id: 'public', name: 'Public' },
  { id: 'private', name: 'Private' },
];
const BILLING_OPTIONS = [
  { id: 'billable', name: 'Billable' },
  { id: 'non-billable', name: 'Non-billable' },
];

/** Sentinel id for the synthetic "No Client" filter option (client-side only). */
const NO_CLIENT_ID = '__no_client__';

interface RowMenuState {
  anchorEl: HTMLElement | null;
  projectId: string | null;
}

const ProjectsPage: React.FC = () => {
  const { canCreateProject, canEditProject, canDeleteProject } = usePermissions();
  const navigate = useNavigate();
  // Each filter has a "pending" value (bound to the control) and an applied value (read
  // by filteredProjects) — Apply copies pending -> applied for all of them at once.
  const [pendingSearch, setPendingSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingStatus, setPendingStatus] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [pendingClient, setPendingClient] = useState<string[]>([]);
  const [clientFilter, setClientFilter] = useState<string[]>([]);
  // Client dropdown's "Show" section — filters by whether the client is archived,
  // independent of which client checkboxes are selected.
  const [pendingClientShow, setPendingClientShow] = useState<'active' | 'archived' | 'all'>(
    'active',
  );
  const [clientShowFilter, setClientShowFilter] = useState<'active' | 'archived' | 'all'>('active');
  const [pendingAccess, setPendingAccess] = useState<string[]>([]);
  const [accessFilter, setAccessFilter] = useState<string[]>([]);
  const [pendingBilling, setPendingBilling] = useState<string[]>([]);
  const [billingFilter, setBillingFilter] = useState<string[]>([]);
  const [applySignal, setApplySignal] = useState(0);
  const [rowMenu, setRowMenu] = useState<RowMenuState>({ anchorEl: null, projectId: null });

  const {
    projects,
    clients,
    isLoading,
    projectModal,
    openCreateModal,
    openEditModal,
    closeModal,
    handleModalSubmit,
    createMutation,
    updateMutation,
    deleteDialog,
    requestDelete,
    confirmDelete,
    cancelDelete,
    deleteMutation,
  } = useProjectActions();

  // Real clients from the API, plus a synthetic "No Client" option — shown only when at
  // least one project has no client assigned.
  const clientOptions = useMemo(() => {
    const base = clients.map((c) => ({ id: c.id, name: c.name, isArchived: c.status !== 'Active' }));
    const hasUnassignedProject = projects.some((p) => p.clientId == null);
    if (!hasUnassignedProject) return base;
    return [...base, { id: NO_CLIENT_ID, name: 'No Client', isArchived: false }];
  }, [clients, projects]);

  const clientArchivedById = useMemo(
    () => new Map(clients.map((c) => [c.id, c.status !== 'Active'])),
    [clients],
  );

  const filteredProjects = useMemo(() => {
    return projects?.filter((project) => {
      const matchesStatus =
        statusFilter.length === 0 ||
        statusFilter.includes(project.status);
      const matchesSearch =
        !searchTerm || project.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesClient =
        clientFilter.length === 0 ||
        (project.clientId != null
          ? clientFilter.includes(project.clientId)
          : clientFilter.includes(NO_CLIENT_ID));
      const clientIsArchived =
        project.clientId != null ? (clientArchivedById.get(project.clientId) ?? false) : false;
      const matchesClientShow =
        clientShowFilter === 'all' ||
        (clientShowFilter === 'archived' ? clientIsArchived : !clientIsArchived);
      const matchesAccess =
        accessFilter.length === 0 || accessFilter.includes(project.isPublic ? 'public' : 'private');
      const matchesBilling =
        billingFilter.length === 0 ||
        billingFilter.includes(project.isBillable ? 'billable' : 'non-billable');
      return (
        matchesStatus &&
        matchesSearch &&
        matchesClient &&
        matchesClientShow &&
        matchesAccess &&
        matchesBilling
      );
    });
  }, [
    projects,
    statusFilter,
    searchTerm,
    clientFilter,
    clientShowFilter,
    clientArchivedById,
    accessFilter,
    billingFilter,
  ]);

  const handleApply = () => {
    setSearchTerm(pendingSearch);
    setStatusFilter(pendingStatus);
    setClientFilter(pendingClient);
    setClientShowFilter(pendingClientShow);
    setAccessFilter(pendingAccess);
    setBillingFilter(pendingBilling);
    setApplySignal((s) => s + 1);
  };

  const handleClientChange = (selected: string[]) => {
    setPendingClient(selected);
  };

  const handleClearFilters = () => {
    setPendingSearch('');
    setPendingStatus([]);
    setPendingClient([]);
    setPendingClientShow('active');
    setPendingAccess([]);
    setPendingBilling([]);
    setSearchTerm('');
    setStatusFilter([]);
    setClientFilter([]);
    setClientShowFilter('active');
    setAccessFilter([]);
    setBillingFilter([]);
  };

  const handleOpenEdit = (projectId: string) => {
    openEditModal(projectId);
    closeRowMenu();
  };

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setRowMenu({ anchorEl: event.currentTarget, projectId });
  };

  const closeRowMenu = () => {
    setRowMenu({ anchorEl: null, projectId: null });
  };


  const handleDeleteClick = () => {
    if (!rowMenu.projectId) return;
    requestDelete(rowMenu.projectId);
    closeRowMenu();
  };

  /** Formats decimal hours as zero-padded HH:MM:SS. */
  const formatTracked = (hours?: number) => {
    if (hours == null) return '—';
    const totalSeconds = Math.max(0, Math.round(hours * 3600));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
  };

  const columns: DataTableColumn<Project>[] = [
    {
      id: 'name',
      label: 'Project',
      render: (project) => (
        <NameCell>
          <ColorDot dotcolor={project.color} />
          <ProjectNameText>{project.name}</ProjectNameText>
        </NameCell>
      ),
    },
    {
      id: 'client',
      label: 'Client',
      render: (project) => <ColWidthBox colwidth={160}>{project.clientName || '—'}</ColWidthBox>,
    },
    {
      id: 'tracked',
      label: 'Tracked',
      render: (project) => (
        <ColWidthBox colwidth={120}>
          <TrackedText>{formatTracked(project.trackedHours)}</TrackedText>
        </ColWidthBox>
      ),
    },
    {
      id: 'access',
      label: 'Access',
      render: (project) => (
        <ColWidthBox colwidth={100}>
          <AccessChip label={project.isPublic ? 'Public' : 'Private'} size="small" />
        </ColWidthBox>
      ),
    },
    {
      id: 'billing',
      label: 'Billing',
      render: (project) => (
        <ColWidthBox colwidth={110}>
          <BillingChip
            label={`$ ${project.isBillable ? 'Billable' : 'Non-billable'}`}
            billable={project.isBillable}
            size="small"
          />
        </ColWidthBox>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (project) => (
        <ColWidthBox colwidth={90}>
          <StatusChip
            label={project.status === 'Active' ? 'Active' : project.status === 'OnHold' ? 'On Hold' : 'Inactive'}
            projectStatus={project.status}
            size="small"
          />
        </ColWidthBox>
      ),
    },
    {
      id: 'actions',
      label: '',
      width: 60,
      align: 'right',
      render: (project) => (
        <ActionCell>
          {(canEditProject || canDeleteProject) && (
            <RowMenuButton
              size="small"
              aria-label="More actions"
              onClick={(e) => {
                e.stopPropagation();
                openRowMenu(e, project.id);
              }}
            >
              <MoreHorizontal size={14} />
            </RowMenuButton>
          )}
        </ActionCell>
      ),
    },
  ];

  const hasFilters =
    Boolean(searchTerm) ||
    statusFilter.length > 0 ||
    clientFilter.length > 0 ||
    clientShowFilter !== 'active' ||
    accessFilter.length > 0 ||
    billingFilter.length > 0;

  return (
    <PageRoot>
      <HeroContainer padding="24px 28px">
        <HeroDecorCircleTop aria-hidden />
        <HeroDecorCircleBottom aria-hidden />
        <HeroContent>
          <div>
            <Typography variant="h4" sx={{ color: '#fff', fontSize: '1.5rem' }}>
              Projects
            </Typography>
            <HeroSubtitle>Manage projects, clients, access, and billing</HeroSubtitle>
          </div>
          {canCreateProject && (
            <HeroActionButton onClick={openCreateModal}>
              <AddIcon sx={{ fontSize: 16 }} />
              New project
            </HeroActionButton>
          )}
        </HeroContent>
      </HeroContainer>

      <FilterWrapper>
        <FilterGroup>
          <SearchFieldBox>
            <SearchIconWrap>
              <SearchIcon sx={{ fontSize: 16 }} />
            </SearchIconWrap>
            <SearchInput
              placeholder="Search projects..."
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
            />
          </SearchFieldBox>

          <SearchableMultiSelect
            label="Client"
            options={clientOptions}
            value={pendingClient}
            onChange={handleClientChange}
            statusField="isArchived"
            onShowFilterChange={setPendingClientShow}
            applySignal={applySignal}
            pill
          />

          <SearchableMultiSelect
            label="Status"
            options={STATUS_OPTIONS}
            value={pendingStatus}
            onChange={setPendingStatus}
            showSelectAll={false}
            pill
          />

          <SearchableMultiSelect
            label="Access"
            options={ACCESS_OPTIONS}
            value={pendingAccess}
            onChange={setPendingAccess}
            showSelectAll={false}
            pill
          />

          <SearchableMultiSelect
            label="Billing"
            options={BILLING_OPTIONS}
            value={pendingBilling}
            onChange={setPendingBilling}
            showSelectAll={false}
            pill
          />

          <ClearFiltersRow>
            {hasFilters && (
              <ClearFiltersLink type="button" onClick={handleClearFilters}>
                Clear filters
              </ClearFiltersLink>
            )}
            <ApplyButton variant="contained" color="primary" size="small" onClick={handleApply}>
              Apply
            </ApplyButton>
          </ClearFiltersRow>
        </FilterGroup>
      </FilterWrapper>

      <NonScrollingTableBox>
        <DataTable<Project>
          columns={columns}
          rows={filteredProjects}
          getRowId={(project) => project.id}
          onRowClick={(id) => navigate(ROUTE_PATHS.PROJECT_DETAIL(id))}
          emptyMessage={isLoading ? 'Loading projects…' : 'No projects match the current filter.'}
          headerBgColor="#F3F0FF"
          headerFontSize="0.6875rem"
          headerTextColor={FILTER_FIELD_TEXT}
          rowHoverColor="rgba(110, 79, 247, 0.03)"
          rowHeight={56}
          cellPaddingX={24}
          containerSx={projectsTableContainerSx}
        />
      </NonScrollingTableBox>

      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={rowMenuSlotProps}
      >
        {canEditProject && (
          <RowMenuItem onClick={() => rowMenu.projectId && handleOpenEdit(rowMenu.projectId)}>
            <Pencil size={16} />
            <ListItemText>Edit</ListItemText>
          </RowMenuItem>
        )}
        {canDeleteProject && canEditProject && <RowMenuDivider />}
        {canDeleteProject && (
          <DeleteMenuItem onClick={handleDeleteClick}>
            <Trash2 size={16} />
            <ListItemText>Delete</ListItemText>
          </DeleteMenuItem>
        )}
      </Menu>

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => confirmDelete()}
        onCancel={cancelDelete}
        loading={deleteMutation.isPending}
      />

      <ProjectFormModal
        open={projectModal.open}
        project={
          projectModal.projectId
            ? (projects.find((p) => p.id === projectModal.projectId) ?? null)
            : null
        }
        clients={clients}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
      />
    </PageRoot>
  );
};

export default ProjectsPage;
